/*
 * Copyright (C) 2021 Bosch.IO GmbH. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Blade } from './Blade';
import { useResizer } from './hooks';
import { Resizer } from './Resizer';
import { BladeProps, ResizerProps } from './types';

/**
 * @name ResizableBlade
 *
 * @param props properties of the blade.
 * @type div
 * @description
 * Extends the plain blade component with the <code>useResizer</code> hook
 */
export const ResizableBlade: React.FC<BladeProps & ResizerProps> = props => {
    const {
        resizable,
        children,
        initialWidth = 350,
        onResetSize,
        onResize,
        collapsed,
        expanded,
        scrollIntoViewDelay = 200,
        onRestore,
        ...rest
    } = props;

    const bladeRef = useRef<HTMLDivElement>();

    useEffect(() => {
        if (props.active) {
            const handle = setTimeout(
                () => bladeRef.current?.scrollIntoView(),
                scrollIntoViewDelay,
            );
            return () => clearTimeout(handle);
        }
    }, [props.active, scrollIntoViewDelay]);

    const [scroll, setScroll] = useState(false);
    useEffect(() => {
        // using effect to execute the scroll after render
        if (scroll) {
            bladeRef.current?.scrollIntoView();
            setScroll(false);
        }
    }, [scroll]);

    function handleRestore() {
        setScroll(true);
        onRestore?.();
    }

    function handleResize(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        resizer.onResize(e);
        onResize && onResize(e);
    }
    function handleReset(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        resizer.onResetSize(e);
        onResetSize && onResetSize(e);
    }

    const resizer = useResizer(initialWidth, bladeRef);
    const enabled = resizable && !collapsed && !expanded;
    return (
        <Blade
            ref={bladeRef}
            footer={
                enabled && (
                    <Resizer
                        onResize={handleResize}
                        onResetSize={handleReset}
                        resizing={resizer.resizing}
                    />
                )
            }
            initialWidth={resizer.width}
            collapsed={collapsed}
            expanded={expanded}
            onRestore={handleRestore}
            {...rest}
        >
            {children}
        </Blade>
    );
};
